import React, { useEffect, useState } from "react";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { Tooltip, tooltipClasses } from "@mui/material";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { all_routes } from "../../../router/all_routes";
import {
  MODULES,
  MODULES_ACTIONS,
} from "../../../../core/data/constants/moduleConstants";
import {
  CALLER,
  COMPANY_ADMIN,
  SUPER_ADMIN,
  TEAM_MANAGER,
} from "../../../../core/data/constants/constant";
import { useCallHandler } from "../../../../utils/hooks/useCallHandler";
import { getAllUrlParams } from "../../../../utils/helpers/setAndGetUrlParams";
import { convertToUTCString } from "../../../../utils/helpers/helper";
import { customMuiDateFilter } from "../../../../utils/helpers/customMuiDateFilter";
import { setFilterModel } from "../../../../core/data/redux/filterSlice";
import { formatLeadsData } from "../../../../utils/helpers/formatModulesData";
import ActionsDropdown from "../../../../core/common/crmComponents/ActionsDropdown";
import SocialMediaIcons from "../../../../core/common/crmComponents/SocialMediaIcons";
import { useFunctionalityAccess } from "../../../../core/common/wrapper/HasFunctionalityAccess";

const DuplicateLeadList = ({ leadsData, selectedCompany, setFilteredData }) => {
  const route = all_routes;
  const { user } = useSelector((state) => state?.auth);

  const [selectedLeadId, setSelectedLeadId] = useState(null);
  const { mutate, isPending, isSuccess } = useCallHandler(
    MODULES.LEADS,
    selectedLeadId
  );
  const filterModel = useSelector((state) => state.filter.filterModel);
  const dispatch = useDispatch();

  const allParams = getAllUrlParams(); // Get all URL params
  const queryString = new URLSearchParams(allParams).toString();

  const hasCallPermission = useFunctionalityAccess("CallPermission");

  // Flatten the leadsData array
  const processedRows = formatLeadsData(leadsData);

  const columns = [
    {
      field: "compaignName",
      headerName: "Compaign Name",
      width: 100,
      renderCell: (params) => (
        <div className="text-capitalize">
          {params.row.compaignName || "N/A"}
        </div>
      ),
    },
    {
      field: "leadSource",
      headerName: "Lead Source",
      width: 130,
      renderCell: (params) => (
        <div className="text-capitalize">
          {params?.row?.leadSource || "N/A"}
        </div>
      ),
    },
    {
      field: "createdAt",
      headerName: "Created At",
      width: 210,
      valueFormatter: (params) =>
        params?.value ? convertToUTCString(params?.value) : "",
      filterOperators: customMuiDateFilter,
      renderCell: (params) => (
        <div>
          {params?.row?.createdTime
            ? convertToUTCString(params?.row?.createdTime)
            : convertToUTCString(params?.row?.lastActivityDate)}
        </div>
      ),
    },
    {
      field: "name",
      headerName: "Client Name",
      width: 100,
      renderCell: (params) => {
        const leadDetailsUrl = `${route.duplicateLeadsDetails.replace(
          ":id",
          params.row.id
        )}?${queryString}`;
        return (
          <Link to={leadDetailsUrl} className="text-capitalize list-link">
            {params.row.firstName} {params.row.lastName}
          </Link>
        );
      },
    },
    {
      field: "assignedTo",
      headerName: "Caller",
      width: 130,
      renderCell: (params) => (
        <div className="text-capitalize">{params.row.assignedTo}</div>
      ),
    },
    {
      field: "mobile",
      headerName: "Phone",
      width: 160,
      renderCell: (params) => (
        <div>
          {hasCallPermission && user?.agentId && user?.campaignName ? (
            <Tooltip
              title="Click To Call"
              placement="bottom"
              slotProps={{
                popper: {
                  sx: {
                    [`&.${tooltipClasses.popper}[data-popper-placement*="bottom"] .${tooltipClasses.tooltip}`]:
                      {
                        marginTop: "0px", // Adjust this to reduce space
                        transform: "translateY(0px)",
                      },
                  },
                },
              }}
            >
              <button
                className="d-flex align-items-center nav-link border-0 bg-transparent"
                onClick={() => {
                  if (!isPending) {
                    setSelectedLeadId(params.id);
                    mutate(params?.row?.mobile);
                  }
                }}
                disabled={isPending}
              >
                <div className="p-0 flex-shrink-0 text-primary me-2 fs-5">
                  {isPending && selectedLeadId === params.id ? (
                    <i className="ti ti-loader spinner" />
                  ) : (
                    <i className="ti ti-phone" />
                  )}
                </div>
                <p className="mb-0">{params?.row?.mobile || "N/A"}</p>
              </button>
            </Tooltip>
          ) : hasCallPermission ? (
            <a
              href={`tel:${params?.row?.mobile}`}
              className="d-flex align-items-center nav-link border-0 bg-transparent "
            >
              <div className="p-0 flex-shrink-0 text-primary me-2 fs-5">
                <i className="ti ti-phone" />
              </div>
              <p className="mb-0">{params?.row?.mobile || "N/A"}</p>
            </a>
          ) : (
            <p className="mb-0">{params?.row?.mobile || "N/A"}</p>
          )}
        </div>
      ),
    },
    {
      field: "",
      headerName: "Social Media",
      width: 100,
      sortable: true,
      renderCell: (params) => {
        return (
          <div className="d-flex gap-2">
            <SocialMediaIcons
              platform="whatsapp"
              phoneNumber={`${params?.row?.mobile}`}
              isIcon={true}
            />
          </div>
        );
      },
    },
    { field: "email", headerName: "Email", width: 210 },
    // {
    //   field: "currentStatus",
    //   headerName: "Status",
    //   width: 110,
    //   sortable: true,
    //   renderCell: (params) => {
    //     return (
    //       <span
    //         className={`badge badge-pill badge-status text-capitalize`}
    //         style={{ backgroundColor: `${params?.row?.currentStatusColor}` }}
    //       >
    //         {params.value}
    //       </span>
    //     );
    //   },
    // },

    {
      field: "salesRemark",
      headerName: "Sales Remark",
      width: 130,
      renderCell: (params) => (
        <div className="text-capitalize">{params.row.salesRemark || "N/A"}</div>
      ),
    },
    {
      field: "description",
      headerName: "Description",
      width: 200,
      renderCell: (params) => (
        <div className="text-capitalize">
          {params?.row?.description || "N/A"}
        </div>
      ),
    },
    {
      field: "city",
      headerName: "City",
      width: 130,
      renderCell: (params) => (
        <div className="text-capitalize">
          {params?.row?.city || params?.row?.address?.city || "N/A"}
        </div>
      ),
    },
    {
      field: "productName",
      headerName: "Product Name",
      width: 130,
      renderCell: (params) => (
        <div className="text-capitalize">
          {params?.row?.productName || "N/A"}
        </div>
      ),
    },
    {
      field: "language",
      headerName: "Language",
      width: 130,
      renderCell: (params) => (
        <div className="text-capitalize">{params?.row?.language || "N/A"}</div>
      ),
    },
    {
      field: "referenceSource",
      headerName: "Reference Source",
      width: 130,
      renderCell: (params) => (
        <div className="text-capitalize">
          {params?.row?.referenceSource || "N/A"}
        </div>
      ),
    },
    {
      field: "probability",
      headerName: "Probability",
      width: 90,
      renderCell: (params) => (
        <div className="text-capitalize">
          {params?.row?.probability ? params?.row?.probability : "medium"}
        </div>
      ),
      sortable: true,
    },

    // {
    //   field: "updatedAt",
    //   headerName: "Updated At",
    //   width: 210,
    //   valueFormatter: (params) =>
    //     params?.value ? convertToUTCString(params?.value) : "",
    //   filterOperators: customMuiDateFilter,
    //   renderCell: (params) => (
    //     <div>
    //       {params?.row?.lastActivityDate
    //         ? convertToUTCString(params?.row?.lastActivityDate)
    //         : convertToUTCString(params?.row?.createdTime)}
    //     </div>
    //   ),
    // },
  ];

  return (
    <div style={{ width: "100%" }}>
      <DataGrid
        rows={processedRows}
        columns={columns.map((column) => ({
          ...column,
          headerClassName: "bg-light-400 ",
        }))}
        autoHeight
        hideFooter
        disableExtendRowFullWidth
        onStateChange={(state) => {
          const filteredRowIds = Object.entries(state.filter.filteredRowsLookup)
            .filter(([_, isVisible]) => isVisible === true)
            .map(([rowId]) => rowId); // Keep IDs as strings

          // Filter leadsData based on these IDs
          const filteredData = leadsData?.filter((row) =>
            filteredRowIds.includes(row?._id)
          );
          // Pass the filtered data to the parent
          // setFilteredData(filteredData);
        }}
        sx={{
          "& .sticky-column": {
            position: "sticky",
            right: 0,
            background: "white", // Prevents content from hiding under it
            zIndex: 100, // Keeps it on top
          },
          "& .MuiDataGrid-columnHeaders": {
            background: "white",
            position: "sticky",
            top: 0,
            zIndex: 110,
          },
          "& .MuiDataGrid-virtualScroller": {
            overflowX: "auto",
          },
        }}
        filterModel={filterModel}
        onFilterModelChange={(newModel) => dispatch(setFilterModel(newModel))}
        slots={{
          toolbar: GridToolbar, // Enable filter toolbar above the grid
        }}
        slotProps={{
          toolbar: {
            printOptions: { disableToolbarButton: true }, // Hide Print Button
            csvOptions: { disableToolbarButton: true }, // Hide CSV Export
          },
        }}
      />
    </div>
  );
};

export default DuplicateLeadList;
